<template>
	<div class="coli cost-of-living-calculator">
		<div class="container py-5">
			<!-- Header -->
			<div class="coli-header row justify-content-between">
				<div class="col-lg-auto">
					<p class="h1 text-uppercase">Cost of Living</p>
					<p>Checkout how prices in your city compare to ours.</p>
				</div>
				<div class="col-lg-auto">
					<div class="form-group row">
						<label for="from" class="col-auto col-form-label"
							>Compared to:</label
						>
						<div class="col-auto">
							<select id="from"
								class="custom-select"
								v-model="compareFromId"
							>
								<option :value="null">Select your City</option>
								<option
									v-for="place in fromPlaces"
									:key="place.Place_Id"
									:value="place.Place_Id"
									>{{ place.FromPlace }}</option
								>
							</select>
						</div>
					</div>
				</div>
			</div>
			<!-- Display -->
			<div class="coli-results" v-if="comparison">
				<!-- Food and Transportation -->
				<div class="row justify-content-lg-end">
					<div class="col-lg-3">
						<p class="small mb-1 text-white">
							Average prices in Cincinnati are:
						</p>
					</div>
				</div>
				<div
					:class="{
						'comparison-group': true,
						'comparison-group--higher': comparison.moreExpensive
					}"
					v-for="comparison of comparisonData"
					:key="comparison.key"
				>
					<div class="row flex-lg-row-reverse">
						<div class="col-lg-3 comparison-text">
							<p class="mb-0">
								<span
									class="compare-percent"
									v-html="comparison.percentText"
								></span>
								<span
									class="compare-text"
									v-html="comparison.compareText"
								></span>
							</p>
						</div>
						<div class="col-lg-9 comparison-graph">
							<p
								class="compare-title compare-category mb-1"
								v-html="comparison.titleText"
							></p>
							<div class="progress">
								<div
									class="progress-bar"
									role="progressbar"
									:style="{ width: comparison.percentText }"
									:aria-valuenow="comparison.percent"
									aria-valuemin="0"
									aria-valuemax="100"
								></div>
							</div>
						</div>
					</div>
				</div>
				<!-- Monthly Housing -->
				<!-- Commute Times -->
				<!-- <div v-for="comparison of compareData" :key="comparison">
					{{ comparison.key }}
					<table class="table table-striped">
						<thead>
							<th
								v-for="col of keys(comparison.data[0])"
								:key="col"
								v-html="col"
							></th>
						</thead>
						<tbody>
							<tr v-for="item of comparison.data" :key="item[0]">
								<td
									v-for="value of values(item)"
									:key="value"
									v-html="value"
								></td>
							</tr>
						</tbody>
					</table>
				</div> -->
			</div>
			<!-- Footnote -->
			<div class="text-right">
				<p class="text-white small mb-0">
					<em
						>All cost of living data provided by
						<a
							class="text-white"
							href="https://www.c2er.org/"
							target="_blank"
							>The Council for Community &amp; Economic
							Research</a
						>.</em
					>
				</p>
			</div>
		</div>
	</div>
</template>

<script>
	/**
	 * C2ER API Integration
	 *
	 * Display Cost of Living information between two cities.
	 *
	 * @devnote Review data display and confirm which information
	 * needs to be displayed. Eliminate unnecessaary API calls.
	 *
	 * @see https://api.c2er.org/costofliving/v3.0/api/swagger/ui/index
	 *
	 * @author LWK
	 */
	const GUID = '{87F7D271-FE5C-45F1-B030-5F99E72DEEC2}';
	const API_BASE_URL = 'https://api.c2er.org:443/costofliving/v3.0/api/api';
	const INDEX_TYPES = {
		COUNTY: 1,
		STATE: 2
	};
	const PLACE_TYPES = {
		FROM: 1,
		TO: 2
	};
	/**
	 * Comparison HashMap
	 * key => api endpoint
	 */
	const COMPARISONS = {
		// Price by Markey Category Item, e.g. Grocery - Steak, Housing - Rent, etc.
		AVERAGE_PRICE: 'GetAveragePriceComparison',
		// Percent of national average for Healthcare, Transportation, etc.
		CATEGORY_BY_PLACE: 'GetCategoryComparisonByPlace',
		/**
		 * Percent difference between from and to cities for Healthcare, Transportation, etc.
		 *
		 * API Response Object
		 * @return ExpenditureCategories {Category: string, DifferenceType: 'more'|'less', PercentageDifference: string}[]
		 */
		EXPENDITURE_CATEGORY: 'GetExpenditureCategoryComparison',
		// Average spending by Homeowner, Renter, w/ kids, etc.
		HOUSEHOLD_MONTHLY_SPENDING:
			'GetTotalMonthlySpendingComparisonByHouseholdType'
	};
	const EXPENDITURE_CATEGORIES = {
		FOOD: 'Grocery',
		TRANSPORT: 'Transportation',
		HOUSING: 'Housing',
		UTILITIES: 'Utilities',
		HEALTHCARE: 'Healthcare',
		MISCELLANEOUS: 'Miscellaneous'
	};
	const EXPENDITURE_CATEGORIES_DISPLAY = {
		Grocery: 'Food',
		Transportation: 'Transportation',
		Housing: 'Housing',
		Utilities: 'Utilities',
		Healthcare: 'Healthcare',
		Miscellaneous: 'Miscellaneous'
	};
	const FILTERED_EXPENDITURE_CATEGORIES = ['Utilities', 'Miscellaneous'];
	const DEFAULT_CITY = 'Chicago';
	export default {
		props: ['defaultCity'],
		data() {
			return {
				COMPARISONS: COMPARISONS,
				EXPENDITURE_CATEGORIES: EXPENDITURE_CATEGORIES,
				loading: true,
				working: false,
				compareFromId: null, // City the user is coming from
				places: {
					from: [],
					to: [] // this should only ever be [Cincinnati]
				},
				// Current place comparison to render
				comparison: null
			};
		},
		computed: {
			// Cincinnati ID
			// { FromPlace: string, FromTotal: number, Place_Id: number }
			compareToId() {
				return this.places.to.length
					? this.places.to[0].Place_Id
					: null;
			},
			fromPlaces() {
				return this.places.from
					.filter(
						p => p.Place_Id !== 1 && p.Place_Id !== this.compareToId
					)
					.sort((a, b) =>
						a.FromPlace == b.FromPlace
							? 0
							: a.FromPlace > b.FromPlace
							? 1
							: -1
					);
			},
			comparisonData() {
				return this.comparison.EXPENDITURE_CATEGORY.filter(
					category =>
						FILTERED_EXPENDITURE_CATEGORIES.indexOf(
							category.Category
						) < 0
				).map(category => ({
					raw: category,
					key: category.Category,
					percent: +category.PercentageDifference.replace('%', ''),
					titleText:
						EXPENDITURE_CATEGORIES_DISPLAY[category.Category],
					percentText: category.PercentageDifference,
					compareText:
						category.DifferenceType == 'less' ? 'Lower' : 'Higher',
					moreExpensive: category.DifferenceType == 'more'
				}));
			},
			compareData() {
				return Object.keys(this.comparison).map(key => ({
					key: key,
					data: this.comparison[key]
				}));
			}
		},
		watch: {
			// Watch for from city change and load new compare data
			compareFromId(newPlaceId, oldPlaceId) {
				if (newPlaceId !== oldPlaceId) {
					this.loadPlaceComparison(newPlaceId, this.compareToId);
				}
			}
		},
		methods: {
			parseResponseData(response) {
				return (
					response.data.dataSet.Table ? response.data.dataSet.Table : response.data.dataSet.Table1
				);
			},
			// Load Place Comparison
			loadPlaceComparison(from, to) {
				this.working = true;
				const promises = this.axios
					.all(
						Object.values(COMPARISONS).map(comparison =>
							this.getComparison(comparison)
						)
					)
					.then(response => {
						// const [averagePrice, category, expenditureCategory, householdMonthlySpending] = response;
						this.comparison = {};
						Object.keys(COMPARISONS).forEach(
							(key, index) =>
								(this.comparison[key] = response[index])
						);
						this.working = false;
					});
			},
			// Get Params for Comparison Call
			getComparisonParams() {
				return this.setParams({
					indexType: INDEX_TYPES.COUNTY,
					fromPlace: this.compareFromId,
					toPlace: this.compareToId
				});
			},
			// Get Available List of FROM Places
			getFromPlaces() {
				return this.axios
					.get(
						`${API_BASE_URL}/getPlaces`,
						this.setParams({
							indexType: INDEX_TYPES.COUNTY,
							placeType: PLACE_TYPES.FROM
						})
					)
					.then(
						response =>
							(this.places.from = this.parseResponseData(
								response
							))
					).then(() => this.setDefaultFromPlace());
			},
			// Get available list of TO places.. should only be Cincinnati
			getToPlaces() {
				return this.axios
					.get(
						`${API_BASE_URL}/getPlaces`,
						this.setParams({
							indexType: INDEX_TYPES.COUNTY,
							placeType: PLACE_TYPES.TO
						})
					)
					.then(
						response =>
							(this.places.to = this.parseResponseData(response))
					);
			},
			setDefaultFromPlace() {
				const defaultCity = this.defaultCity ? this.defaultCity : DEFAULT_CITY ;
				if (defaultCity) {
					const searchForCity = defaultCity.toLowerCase();
					const foundDefaultCity = this.places.from.find(city => city.FromPlace.toLowerCase().indexOf(searchForCity) > -1);
					if (foundDefaultCity) {
						console.log('Setting default City', foundDefaultCity);
						this.compareFromId = foundDefaultCity.Place_Id;
					}
				}
			},
			setParams(_params) {
				return {
					params: {
						licenseeGuid: GUID,
						..._params
					}
				};
			},
			keys(obj) {
				return Object.keys(obj);
			},
			values(obj) {
				return Object.values(obj);
			},
			/**
			 * API Calls
			 *
			 */
			getComparison(comparison) {
				return this.axios
					.get(
						`${API_BASE_URL}/${comparison}`,
						this.getComparisonParams()
					)
					.then(response => {
						return this.parseResponseData(response);
					});
			},
			// Salary comparison requires additional input
			// This is currently unused
			getSalaryComparison(currentSalary) {
				const options = this.getComparisonParams();
				options.params.currentBaseSalary = +currentSalary;
				return this.axios
					.get(`${API_BASE_URL}/GetSalaryComparison`, options)
					.then(response => {
						return this.parseResponseData(response);
					});
			}
		},
		created() {
			const promises = this.axios
				.all([this.getFromPlaces(), this.getToPlaces()])
				.then(loaded => (this.loading = false));
		}
	};
</script>

<style scoped lang="scss">
	$gray: #2f2f2f;
	$gold: #f6c8a1;
	$plum: #956589;
	.coli {
		background-color: $gray;

		.comparison-group {
			margin: 1.5rem 0;
			.comparison-text {
				color: $gold;
				font-size: 1rem;
				font-weight: bold;
				letter-spacing: 2px;
				p {
					display: flex;
					align-items: center;
				}
				.compare-percent {
					font-size: 2.5rem;
					margin-right: 0.5rem;
				}
				.compare-text {
					text-transform: uppercase;
				}
			}
			.comparison-graph {
				.compare-title {
				}
			}
			.progress {
				height: 8px;
				border-radius: 4px;
				.progress-bar {
					background-color: $gold;
				}
			}
			&.comparison-group--higher {
				.comparison-text {
					color: white;
				}
				.progress .progress-bar {
					background-color: $plum;
				}
			}
		}
	}
</style>
